import { useEffect } from 'react';

import dayjs, { Dayjs } from 'dayjs';
import { DutyHourTypeDynFlag } from 'generated';
import { Control, UseFormWatch, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InferType } from 'yup';

import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';

import { ControlledTimePicker } from 'components/shared';
import ControlledDutyHourTypeSelector from 'components/shared/ControlledFields/ControlledDutyHourTypeSelector';

import getPeriodsSchema from './ControlledTimePeriodsSchema';
import {
  GridContainer,
  GridCompact10,
  GridCompact12,
  GridCompact2
} from 'components/shared/Grid/Grid.components';
import { rgbaOrange, rgbaPrimary } from 'constants/colors';
import { Icon } from '@sdb/planning-ui';
import { timeToBaseDate } from './ControlledTimePeriods.utils';

type TimePeriodsProps = {
  control: Control<any>;
  hasAddPeriods?: boolean;
  hasDHTSelector?: boolean;
  hideBreakDuration?: boolean;
  dutyHourTypeDynFlags?: DutyHourTypeDynFlag[];
  referenceDate?: Date;
  watch: UseFormWatch<any>;
  baseDate?: Dayjs;
  checkRestrictedAuthorization?: boolean;
  disabled?: boolean;
  fixTimeToBaseDate?: boolean;
};

export type PeriodsForm = InferType<ReturnType<typeof getPeriodsSchema>>;

type Period = {
  dutyCodePeriodId?: number;
  startTime: Dayjs;
  endTime: Dayjs;
  dutyHourTypeId: number;
};

type FormValues = {
  periods: Period[];
};

export default function TimePeriods({
  control,
  hasAddPeriods = true,
  hasDHTSelector,
  hideBreakDuration,
  dutyHourTypeDynFlags,
  referenceDate,
  watch,
  baseDate: b,
  checkRestrictedAuthorization,
  disabled,
  fixTimeToBaseDate = false
}: TimePeriodsProps) {
  const { t } = useTranslation('common');
  const baseDate = (b || dayjs('1969-12-31T23:00:00Z')).startOf('day');

  const {
    fields: periods,
    append,
    remove,
    update
  } = useFieldArray<FormValues, 'periods', 'id'>({
    name: 'periods',
    control
  });

  function onAddClick() {
    const lastPeriodEndTime =
      watch(`periods.${periods.length - 1}.endTime`) ?? dayjs().startOf('hour');

    const value = {
      dutyCodePeriodId: undefined,
      startTime: lastPeriodEndTime,
      endTime: lastPeriodEndTime.add(1, 'hour'),
      dutyHourTypeId: -1,
      dutyHourType: undefined
    };

    append(value);
  }

  function changeTime(time: Dayjs | null, index: number, name: string) {
    if (time && time.isValid()) {
      let lastTime = baseDate;
      let oneDayAhead = lastTime.add(1, 'day');
      for (let i = 0; i < periods.length; i++) {
        let changed = false;
        if (index === i && name === 'startTime') {
          periods[i].startTime = time;
          changed = true;
        }
        if (!periods[i].startTime.isBefore(oneDayAhead)) {
          periods[i].startTime = periods[i].startTime.subtract(1, 'day');
          changed = true;
        }
        if (periods[i].startTime.isBefore(lastTime)) {
          periods[i].startTime = periods[i].startTime.add(1, 'day');
          changed = true;
        }
        lastTime = periods[i].startTime;
        oneDayAhead = lastTime.add(1, 'day');

        if (index === i && name === 'endTime') {
          periods[i].endTime = time;
          changed = true;
        }
        if (periods[i].endTime.isAfter(oneDayAhead)) {
          periods[i].endTime = periods[i].endTime.subtract(1, 'day');
          changed = true;
        }
        if (!periods[i].endTime.isAfter(lastTime)) {
          periods[i].endTime = periods[i].endTime.add(1, 'day');
          changed = true;
        }
        lastTime = periods[i].endTime;
        oneDayAhead = lastTime.add(1, 'day');
        if (changed) {
          update(i, periods[i]);
        }
      }
    }
  }
  useEffect(() => {
    changeTime(periods[0].startTime, -1, 'startTime');
  }, []);

  const timePeriods = fixTimeToBaseDate
    ? periods.map((period) => {
        return {
          ...period,
          startTime: timeToBaseDate(period.startTime, baseDate),
          endTime: timeToBaseDate(period.endTime, baseDate)
        };
      })
    : periods;

  return (
    <Box mt={1.25}>
      {timePeriods.map((period, i) => {
        return (
          <GridContainer key={period.id}>
            <Grid size={{ compact: hasDHTSelector ? 3 : 5 }}>
              <ControlledTimePicker
                fullWidth
                variant="outlined"
                control={control}
                name={`periods.${i}.startTime`}
                label={t('startTime')}
                onChange={(time) => changeTime(time, i, 'startTime')}
                baseDate={baseDate}
                disabled={disabled}
                fixTimeToBaseDate={fixTimeToBaseDate}
              />
            </Grid>
            <Grid size={{ compact: hasDHTSelector ? 3 : 5 }}>
              <ControlledTimePicker
                fullWidth
                variant="outlined"
                control={control}
                name={`periods.${i}.endTime`}
                label={t('endTime')}
                onChange={(time) => changeTime(time, i, 'endTime')}
                baseDate={baseDate}
                disabled={disabled}
                fixTimeToBaseDate={fixTimeToBaseDate}
              />
            </Grid>
            <GridContainer size={{ compact: 6 }}>
              <GridCompact10>
                {hasDHTSelector && (
                  <ControlledDutyHourTypeSelector
                    control={control}
                    name={`periods.${i}.dutyHourTypeId`}
                    label={t('dutyHourType')}
                    fullWidth
                    filterDutyHourTypeDynFlags={dutyHourTypeDynFlags}
                    referenceDate={referenceDate}
                    checkRestrictedAuthorization={checkRestrictedAuthorization}
                    disabled={disabled}
                  />
                )}
              </GridCompact10>
              <GridCompact2>
                <IconButton
                  onClick={() => remove(i)}
                  color="primary"
                  disabled={disabled}
                >
                  <Icon type="xmark" fontSize="small" />
                </IconButton>
              </GridCompact2>
            </GridContainer>

            {!hideBreakDuration &&
              i < periods.length - 1 &&
              (() => {
                const hasBreakPeriod =
                  periods[i + 1].startTime.diff(period.endTime, 'minute') > 0;

                return (
                  <GridCompact12
                    sx={{
                      bgcolor: hasBreakPeriod
                        ? rgbaOrange(0.08)
                        : rgbaPrimary(0.05),
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `1px dashed ${
                        hasBreakPeriod ? rgbaOrange(0.45) : rgbaPrimary(0.5)
                      }`,
                      mb: (theme) => theme.spacing(5),
                      borderRadius: (theme) => theme.spacing(2)
                    }}
                  >
                    {hasBreakPeriod ? (
                      <>
                        {periods[i + 1].startTime.diff(
                          period.endTime,
                          'minute'
                        )}
                        m {t('pauseBreak')}
                      </>
                    ) : (
                      t('noBreak')
                    )}
                  </GridCompact12>
                );
              })()}
            {hideBreakDuration && <GridCompact12 />}
          </GridContainer>
        );
      })}
      {hasAddPeriods && !disabled && (
        <GridCompact12 mt={2}>
          <Button
            variant="outlined"
            onClick={onAddClick}
            startIcon={<Icon type="plus" fontSize="small" />}
          >
            {t('addTimes')}
          </Button>
        </GridCompact12>
      )}
    </Box>
  );
}
